import type { AccountKind, DeviceAccount, DeviceAccounts } from './types.ts'

// The accounts open on this device, as one document in the refresh token's slot. Why one document, and the
// bare token of before: packages/auth/README.md § «Più account sullo stesso telefono».

const VERSION = 1

export interface StoredAccount extends DeviceAccount {
  readonly refreshToken: string
}

export interface AccountsDocument {
  /** The one a reload brings back. Each tab keeps its own in memory: this is the last one chosen. */
  readonly active: string | null
  readonly accounts: readonly StoredAccount[]
}

export type SlotContent =
  | { readonly kind: 'document'; readonly document: AccountsDocument }
  | { readonly kind: 'bare-token'; readonly refreshToken: string }

export const NO_ACCOUNTS: AccountsDocument = { active: null, accounts: [] }

const KINDS: readonly AccountKind[] = ['own', 'delegated']

function isStoredAccount(value: unknown): value is StoredAccount {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as Partial<StoredAccount>
  return (
    typeof candidate.sub === 'string' &&
    typeof candidate.refreshToken === 'string' &&
    typeof candidate.email === 'string' &&
    KINDS.includes(candidate.kind as AccountKind)
  )
}

function documentOf(raw: string): AccountsDocument {
  try {
    const parsed = JSON.parse(raw) as { version?: unknown; active?: unknown; accounts?: unknown }
    if (parsed.version !== VERSION || !Array.isArray(parsed.accounts)) return NO_ACCOUNTS
    const accounts = parsed.accounts.filter(isStoredAccount)
    const active = accounts.some((account) => account.sub === parsed.active)
      ? (parsed.active as string)
      : null
    return { active, accounts }
  } catch {
    // A slot nobody can read signs nobody in: starting empty beats refreshing with garbage.
    return NO_ACCOUNTS
  }
}

export function readSlot(raw: string | null): SlotContent {
  if (raw === null || raw === '') return { kind: 'document', document: NO_ACCOUNTS }
  // A Cognito refresh token is a JWE, never JSON: the first character tells the two formats apart.
  if (!raw.startsWith('{')) return { kind: 'bare-token', refreshToken: raw }
  return { kind: 'document', document: documentOf(raw) }
}

/** `null` when nothing is left: the slot is cleared rather than holding an empty document. */
export function slotOf(document: AccountsDocument): string | null {
  if (document.accounts.length === 0) return null
  return JSON.stringify({ version: VERSION, active: document.active, accounts: document.accounts })
}

export function findAccount(
  document: AccountsDocument,
  sub: string | null,
): StoredAccount | undefined {
  return sub === null ? undefined : document.accounts.find((account) => account.sub === sub)
}

/** The one in use after a sign-in: the account itself, or the one before; nobody when it was the only one. */
function activeAfter(
  document: AccountsDocument,
  account: StoredAccount,
  options: { readonly activate: boolean; readonly alone: boolean },
): string | null {
  if (options.activate) return account.sub
  return options.alone ? null : document.active
}

/** Replaces the account with the same `sub`, in place, or appends it. With `alone` it is the only one. */
export function withAccount(
  document: AccountsDocument,
  account: StoredAccount,
  options: { readonly activate: boolean; readonly alone: boolean },
): AccountsDocument {
  const others = options.alone ? [] : document.accounts
  const accounts = others.some((held) => held.sub === account.sub)
    ? others.map((held) => (held.sub === account.sub ? account : held))
    : [...others, account]
  return { active: activeAfter(document, account, options), accounts }
}

export function withRefreshToken(
  document: AccountsDocument,
  sub: string,
  refreshToken: string,
): AccountsDocument {
  return {
    ...document,
    accounts: document.accounts.map((held) =>
      held.sub === sub ? { ...held, refreshToken } : held,
    ),
  }
}

export function withActive(document: AccountsDocument, sub: string | null): AccountsDocument {
  return { ...document, active: sub }
}

export function withoutAccount(document: AccountsDocument, sub: string): AccountsDocument {
  return {
    active: document.active === sub ? null : document.active,
    accounts: document.accounts.filter((held) => held.sub !== sub),
  }
}

/** What the app may see: no refresh token leaves the package. */
export function publicAccount(stored: StoredAccount): DeviceAccount {
  const { sub, kind, email, name, personId, addedBy } = stored
  return {
    sub,
    kind,
    email,
    ...(name === undefined ? {} : { name }),
    ...(personId === undefined ? {} : { personId }),
    ...(addedBy === undefined ? {} : { addedBy }),
  }
}

export function publicView(document: AccountsDocument, active: string | null): DeviceAccounts {
  return { active, list: document.accounts.map(publicAccount) }
}
